// Split from djValueAdd.ts (I2). Shared local runtime helpers kept per-file for isolation.
import { SeqCounter } from "@djsem/shared";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 41 — Smart Crate Builder
// ============================================================================

/** The purpose/category of a smart crate, informing how it's presented and refreshed. */
export type DJSEMSmartCrateType =
  | "dynamic" | "saved_snapshot" | "event_brief_based" | "set_prep"
  | "release_prep" | "maintenance" | "transition_tools" | "visual_ready" | "custom";

/** A track metadata field that a smart crate rule can filter or sort on. */
export type DJSEMSmartCrateField =
  | "genre" | "bpm" | "key" | "energy" | "mood" | "artist" | "title"
  | "explicit" | "vocal_density" | "lyrics_available" | "stem_available"
  | "analysis_confidence" | "visual_readiness" | "source_playlist"
  | "manual_tag" | "platform_ref" | "event_fit" | "created_at" | "updated_at";

/** Comparison operator used to evaluate a smart crate rule against a track's field value. */
export type DJSEMSmartCrateOperator =
  | "equals" | "not_equals" | "contains" | "not_contains"
  | "greater_than" | "less_than" | "between" | "exists" | "missing" | "in" | "not_in";

/** How matched tracks are ordered within a smart crate. */
export type DJSEMSmartCrateSortMode =
  | "energy_ascending" | "energy_descending" | "bpm_ascending" | "bpm_descending"
  | "key_compatible" | "recently_added" | "confidence_descending" | "custom";

/** A single filter condition used to select tracks into a smart crate. */
export interface DJSEMSmartCrateRule {
  id: string;
  field: DJSEMSmartCrateField;
  operator: DJSEMSmartCrateOperator;
  value: unknown;
  weight?: number;
  required: boolean;
}

/** A rule-based (optionally dynamic) collection of tracks for a project or workflow. */
export interface DJSEMSmartCrate {
  id: string;
  projectId?: string;
  name: string;
  description?: string;
  crateType: DJSEMSmartCrateType;
  rules: DJSEMSmartCrateRule[];
  sortMode: DJSEMSmartCrateSortMode;
  trackIds: string[];
  dynamic: boolean;
  sourceEventBriefId?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  warnings: string[];
}

/** A single library track evaluated against a smart crate's rules, with its match score and explanation. */
export interface DJSEMSmartCrateCandidate {
  trackId: string;
  score: number;
  matchedRules: string[];
  failedRules: string[];
  explanation: string;
  warnings: string[];
}

/** The result of evaluating a smart crate's rules against a library of tracks. */
export interface DJSEMSmartCrateBuildReport {
  id: string;
  crateId: string;
  totalLibraryTracks: number;
  matchedTracks: number;
  rejectedTracks: number;
  candidates: DJSEMSmartCrateCandidate[];
  warnings: string[];
  createdAt: string;
}

/** Track shape for crate evaluation (minimal — reuses existing track metadata). */
export interface DJSEMCrateTrackInput {
  id: string;
  title?: string; artist?: string;
  bpm?: number; key?: string; energy?: number;
  genre?: string; mood?: string;
  explicit?: boolean;
  tags?: string[];
  hasLyrics?: boolean; hasStem?: boolean;
  analysisConfidence?: number;
}

function evaluateRule(rule: DJSEMSmartCrateRule, track: DJSEMCrateTrackInput): boolean {
  const val = (track as unknown as Record<string, unknown>)[rule.field];
  switch (rule.operator) {
    case "equals": return val === rule.value;
    case "not_equals": return val !== rule.value;
    case "contains": return typeof val === "string" && typeof rule.value === "string" && val.toLowerCase().includes(rule.value.toLowerCase());
    case "not_contains": return typeof val === "string" && typeof rule.value === "string" && !val.toLowerCase().includes(rule.value.toLowerCase());
    case "greater_than": return typeof val === "number" && typeof rule.value === "number" && val > rule.value;
    case "less_than": return typeof val === "number" && typeof rule.value === "number" && val < rule.value;
    case "between": {
      if (typeof val !== "number" || !Array.isArray(rule.value) || rule.value.length !== 2) return false;
      return val >= (rule.value[0] as number) && val <= (rule.value[1] as number);
    }
    case "exists": return val !== undefined && val !== null;
    case "missing": return val === undefined || val === null;
    case "in": return Array.isArray(rule.value) && (rule.value as unknown[]).includes(val);
    case "not_in": return Array.isArray(rule.value) && !(rule.value as unknown[]).includes(val);
    default: return false;
  }
}

/** Input required to create a new {@link DJSEMSmartCrate}. */
export interface CreateSmartCrateInput extends Omit<DJSEMSmartCrate, "id" | "createdAt" | "updatedAt" | "trackIds" | "warnings"> {
  id?: string;
}

/** Creates, evaluates, and refreshes smart crates against a track library. */
export class DJSEMSmartCrateService {
  /**
   * Creates a new smart crate from the given definition, with an empty track list.
   * @param input - the crate's name, type, rules, and sort mode.
   * @returns the newly created smart crate.
   */
  createCrate(input: CreateSmartCrateInput): DJSEMSmartCrate {
    return { ...input, id: input.id ?? `sc_${_seq.next()}`, trackIds: [], warnings: [], createdAt: AT, updatedAt: AT };
  }

  /**
   * Evaluates a crate's rules against a library of tracks, scoring and ranking matches.
   * @param crate - the smart crate whose rules to apply.
   * @param library - the candidate tracks to evaluate.
   * @returns a build report listing matched/rejected counts and ranked candidates.
   */
  evaluateCrate(crate: DJSEMSmartCrate, library: DJSEMCrateTrackInput[]): DJSEMSmartCrateBuildReport {
    const candidates: DJSEMSmartCrateCandidate[] = [];
    for (const track of library) {
      const matched: string[] = [];
      const failed: string[] = [];
      let blocked = false;
      for (const rule of crate.rules) {
        if (evaluateRule(rule, track)) { matched.push(rule.id); }
        else { failed.push(rule.id); if (rule.required) blocked = true; }
      }
      const score = crate.rules.length > 0 ? matched.length / crate.rules.length : 0;
      const warnings: string[] = [];
      if (track.bpm === undefined) warnings.push("Missing BPM");
      if (track.key === undefined) warnings.push("Missing key");
      if (!blocked) candidates.push({ trackId: track.id, score, matchedRules: matched, failedRules: failed, explanation: `Matched ${matched.length}/${crate.rules.length} rules`, warnings });
    }
    candidates.sort((a, b) => b.score - a.score);
    return {
      id: `scr_${_seq.next()}`, crateId: crate.id,
      totalLibraryTracks: library.length, matchedTracks: candidates.length,
      rejectedTracks: library.length - candidates.length,
      candidates, warnings: [], createdAt: AT,
    };
  }

  /**
   * Re-evaluates a dynamic crate's rules and updates its track list; no-op for non-dynamic crates.
   * @param crate - the crate to refresh.
   * @param library - the current track library to re-evaluate against.
   * @returns the crate unchanged if not dynamic, otherwise a copy with `trackIds` refreshed from the latest matches.
   */
  refreshDynamicCrate(crate: DJSEMSmartCrate, library: DJSEMCrateTrackInput[]): DJSEMSmartCrate {
    if (!crate.dynamic) return crate;
    const report = this.evaluateCrate(crate, library);
    return { ...crate, trackIds: report.candidates.map(c => c.trackId), updatedAt: AT };
  }

  /**
   * Freezes a (typically dynamic) crate into a static, non-dynamic saved snapshot.
   * @param crate - the crate to snapshot.
   * @returns a new crate copy with a fresh id, `dynamic: false`, and `crateType: "saved_snapshot"`.
   */
  snapshotCrate(crate: DJSEMSmartCrate): DJSEMSmartCrate {
    return { ...crate, id: `sc_snap_${_seq.next()}`, dynamic: false, crateType: "saved_snapshot", updatedAt: AT };
  }
}

/**
 * Returns a set of common starter smart crate templates (warmup, peak time, clean tracks, etc.)
 * that callers can use to seed new crates.
 * @returns an array of template name/rules pairs.
 */
export function createStarterCrateTemplates(): Array<{ name: string; rules: DJSEMSmartCrateRule[] }> {
  return [
    { name: "Warmup — Low/Medium Energy", rules: [{ id: "r1", field: "energy", operator: "less_than", value: 0.6, required: false }] },
    { name: "Peak Time — High Energy", rules: [{ id: "r2", field: "energy", operator: "greater_than", value: 0.7, required: false }] },
    { name: "Clean Tracks", rules: [{ id: "r3", field: "explicit", operator: "equals", value: false, required: true }] },
    { name: "Wedding Safe", rules: [{ id: "r4", field: "explicit", operator: "equals", value: false, required: true }] },
    { name: "Needs Metadata Review", rules: [{ id: "r5", field: "bpm", operator: "missing", value: null, required: false }, { id: "r6", field: "key", operator: "missing", value: null, required: false }] },
  ];
}
